import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga } from '@/components/Estados';
import { useOpcionesLicencia } from '@/features/alumnos/queries';
import { mxn } from '@/lib/formato';
import { cn } from '@/lib/utils';

import {
  cuerpoLicencia,
  erroresSitioLicencia,
  slugDe,
  useCatalogoPermisos,
  useLicencia,
  type LicenciaBody,
  type LicenciaDetalle,
} from './queries';

const PESTANAS = [
  ['general', 'Datos generales'],
  ['descripcion', 'Descripción'],
  ['permisos', 'Permisos'],
  ['obsequios', 'Obsequios'],
  ['equipos', 'Precios por equipo'],
  ['appstore', 'App Store'],
] as const;
type Pestana = (typeof PESTANAS)[number][0];

const VACIA: LicenciaBody = {
  name: '',
  type: 'curso',
  description: '',
  price: '',
  finalPrice: '',
  classLimit: 0,
  liveHistoryLimit: 0,
  enforcesClassLimit: false,
  slug: null,
  installments: null,
  accessEndsOn: null,
};

/** Campos que se mandan en PATCH/POST (los de la pestaña general, descripción y App Store). */
function cuerpo(d: LicenciaDetalle | null): LicenciaBody {
  if (!d) return { ...VACIA };
  return {
    code: d.code,
    name: d.name,
    type: d.type,
    description: d.description,
    appStoreDescription: d.appStoreDescription,
    price: d.price,
    finalPrice: d.finalPrice,
    appStorePrice: d.appStorePrice,
    appStoreProductId: d.appStoreProductId,
    googlePlayProductId: d.googlePlayProductId,
    classLimit: d.classLimit,
    liveHistoryLimit: d.liveHistoryLimit,
    enforcesClassLimit: d.enforcesClassLimit,
    isActive: d.isActive,
    isVisibleInStore: d.isVisibleInStore,
    isComingSoon: d.isComingSoon,
    isSoldOut: d.isSoldOut,
    hasPulse: d.hasPulse,
    lastSpotsEnabled: d.lastSpotsEnabled,
    lastSpotsCount: d.lastSpotsCount,
    isGiftable: d.isGiftable,
    isInstructor: d.isInstructor,
    sortOrder: d.sortOrder,
    onsiteFrom: d.onsiteFrom,
    onsiteTo: d.onsiteTo,
    scheduleStart: d.scheduleStart,
    scheduleEnd: d.scheduleEnd,
    joinMinutesBefore: d.joinMinutesBefore,
    slug: d.slug,
    installments: d.installments,
    accessEndsOn: d.accessEndsOn,
  };
}

function Interruptor({
  etiqueta,
  valor,
  onCambio,
  deshabilitado,
}: {
  etiqueta: string;
  valor: boolean | undefined;
  onCambio: (v: boolean) => void;
  deshabilitado: boolean;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 accent-[var(--color-azul-600)]"
        checked={valor ?? false}
        disabled={deshabilitado}
        onChange={(e) => onCambio(e.target.checked)}
      />
      {etiqueta}
    </label>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="panel-vidrio space-y-4">
      <h2 className="text-lg font-extrabold">{titulo}</h2>
      {children}
    </section>
  );
}

function Editor({ actual }: { actual: LicenciaDetalle | null }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editable = useCan('tienda', 'editar');
  const solo = !editable;
  const [params, setParams] = useSearchParams();
  const pestana = (
    actual && PESTANAS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'general'
  ) as Pestana;
  const [f, setF] = useState<LicenciaBody>(() => cuerpo(actual));
  const poner = (c: Partial<LicenciaBody>) => setF((v) => ({ ...v, ...c }));
  // En una licencia nueva el slug sigue al nombre hasta que se edita a mano.
  const [slugManual, setSlugManual] = useState(actual !== null);
  const erroresSitio = erroresSitioLicencia(f);
  const permisosCat = useCatalogoPermisos();
  const opciones = useOpcionesLicencia();
  const [permisos, setPermisos] = useState<string[]>(actual?.permissions ?? []);
  const [regalos, setRegalos] = useState<number[]>(actual?.gifts ?? []);
  const [tiers, setTiers] = useState(
    (actual?.teamTiers ?? []).map((t) => ({
      membersCount: t.membersCount,
      finalPrice: t.finalPrice,
    })),
  );

  const refrescar = (d: LicenciaDetalle) => {
    qc.setQueryData(['tienda', 'licencia', d.id], d);
    void qc.invalidateQueries({ queryKey: ['tienda', 'licencias'] });
    void qc.invalidateQueries({ queryKey: ['licencias', 'opciones'] });
  };
  const ok = (msg: string) => (d: LicenciaDetalle) => {
    refrescar(d);
    toast.success(msg);
  };
  const err = (e: unknown) => toast.error(mensajeDe(e));
  const ruta = { params: { path: { id: String(actual?.id) } } };

  const guardar = useMutation({
    mutationFn: () => {
      const body = cuerpoLicencia(f);
      return actual
        ? unwrap<LicenciaDetalle>(api.PATCH('/api/v1/admin/memberships/{id}', { ...ruta, body }))
        : unwrap<LicenciaDetalle>(api.POST('/api/v1/admin/memberships', { body }));
    },
    onSuccess: (d) => {
      refrescar(d);
      toast.success(actual ? 'Licencia guardada.' : 'Licencia creada (inactiva).');
      if (!actual) void navigate(`/tienda/licencias/${d.id}`, { replace: true });
    },
    onError: err,
  });
  const duplicar = useMutation({
    mutationFn: () =>
      unwrap<LicenciaDetalle>(api.POST('/api/v1/admin/memberships/{id}/duplicate', ruta)),
    onSuccess: (d) => {
      refrescar(d);
      toast.success('Licencia duplicada.');
      void navigate(`/tienda/licencias/${d.id}`);
    },
    onError: err,
  });
  const guardarPermisos = useMutation({
    mutationFn: () =>
      unwrap<LicenciaDetalle>(
        api.PUT('/api/v1/admin/memberships/{id}/permissions', {
          ...ruta,
          body: { permissions: permisos },
        }),
      ),
    onSuccess: ok('Permisos guardados.'),
    onError: err,
  });
  const guardarRegalos = useMutation({
    mutationFn: () =>
      unwrap<LicenciaDetalle>(
        api.PUT('/api/v1/admin/memberships/{id}/gifts', { ...ruta, body: { gifts: regalos } }),
      ),
    onSuccess: ok('Obsequios guardados.'),
    onError: err,
  });
  const guardarTiers = useMutation({
    mutationFn: () =>
      unwrap<LicenciaDetalle>(
        api.PUT('/api/v1/admin/memberships/{id}/team-tiers', { ...ruta, body: { tiers } }),
      ),
    onSuccess: ok('Precios por equipo guardados.'),
    onError: err,
  });

  const descuento =
    Number(f.price) > 0 && Number(f.finalPrice) < Number(f.price)
      ? Math.round(((Number(f.price) - Number(f.finalPrice)) * 10000) / Number(f.price)) / 100
      : 0;
  const grupos = Object.entries(
    (permisosCat.data ?? []).reduce<Record<string, { key: string; name: string }[]>>((acc, p) => {
      (acc[p.group] ??= []).push(p);
      return acc;
    }, {}),
  );
  const botonGuardar = editable && (
    <button
      type="button"
      className="btn-cs btn-primario"
      disabled={guardar.isPending}
      onClick={() => {
        const error = erroresSitio.slug ?? erroresSitio.installments;
        if (error) {
          toast.error(`Revisa los datos del sitio público: ${error}`);
          return;
        }
        guardar.mutate();
      }}
    >
      {guardar.isPending ? 'Guardando…' : actual ? 'Guardar' : 'Crear licencia'}
    </button>
  );

  return (
    <>
      <Link
        to="/tienda/licencias"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft size={16} aria-hidden /> Licencias
      </Link>
      <Encabezado
        titulo={actual ? actual.name : 'Nueva licencia'}
        descripcion={
          actual
            ? `#${actual.id} · ${mxn(actual.finalPrice)} · ${actual.isActive ? 'activa' : 'inactiva'} · ${actual.libraryItemsCount} contenidos de biblioteca`
            : 'Se crea inactiva; actívala cuando esté lista.'
        }
        acciones={
          actual &&
          editable && (
            <button
              type="button"
              className="btn-cs btn-contorno"
              onClick={() => duplicar.mutate()}
              disabled={duplicar.isPending}
            >
              <Copy size={16} aria-hidden /> Duplicar
            </button>
          )
        }
      />
      {actual && (
        <div
          role="tablist"
          aria-label="Secciones de la licencia"
          className="mb-5 flex flex-wrap gap-2"
        >
          {PESTANAS.map(([k, texto]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={pestana === k}
              className={cn('btn-cs', pestana === k ? 'btn-primario' : 'btn-vidrio')}
              onClick={() => setParams(k === 'general' ? {} : { tab: k }, { replace: true })}
            >
              {texto}
            </button>
          ))}
        </div>
      )}

      {pestana === 'general' && (
        <div className="grid gap-6 xl:grid-cols-2">
          <Seccion titulo="Datos y precio">
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo etiqueta="Nombre">
                <input
                  className="input-vidrio"
                  maxLength={50}
                  disabled={solo}
                  value={f.name}
                  onChange={(e) =>
                    poner(
                      slugManual
                        ? { name: e.target.value }
                        : { name: e.target.value, slug: slugDe(e.target.value) || null },
                    )
                  }
                />
              </Campo>
              <Campo etiqueta="Código (opcional)">
                <input
                  className="input-vidrio"
                  maxLength={30}
                  disabled={solo}
                  value={f.code ?? ''}
                  onChange={(e) => poner({ code: e.target.value || null })}
                />
              </Campo>
              <Campo etiqueta="Tipo">
                <input
                  className="input-vidrio"
                  list="tipos-licencia"
                  disabled={solo}
                  value={f.type}
                  onChange={(e) => poner({ type: e.target.value })}
                />
                <datalist id="tipos-licencia">
                  <option value="curso" />
                  <option value="simulador" />
                  <option value="presencial" />
                  <option value="app" />
                </datalist>
              </Campo>
              <Campo etiqueta="Orden en la tienda">
                <input
                  type="number"
                  className="input-vidrio"
                  disabled={solo}
                  value={f.sortOrder ?? 0}
                  onChange={(e) => poner({ sortOrder: Number(e.target.value) })}
                />
              </Campo>
              <Campo etiqueta="Precio (MXN)">
                <input
                  className="input-vidrio"
                  inputMode="decimal"
                  disabled={solo}
                  value={f.price}
                  onChange={(e) => poner({ price: e.target.value })}
                />
              </Campo>
              <Campo
                etiqueta="Precio final (MXN)"
                ayuda={descuento > 0 ? `Descuento de ${descuento}%` : undefined}
              >
                <input
                  className="input-vidrio"
                  inputMode="decimal"
                  disabled={solo}
                  value={f.finalPrice}
                  onChange={(e) => poner({ finalPrice: e.target.value })}
                />
              </Campo>
            </div>
          </Seccion>
          <Seccion titulo="Estado en la tienda">
            <div className="grid gap-2 sm:grid-cols-2">
              <Interruptor
                etiqueta="Activa"
                valor={f.isActive}
                onCambio={(v) => poner({ isActive: v })}
                deshabilitado={solo}
              />
              <Interruptor
                etiqueta="Visible en la tienda"
                valor={f.isVisibleInStore}
                onCambio={(v) => poner({ isVisibleInStore: v })}
                deshabilitado={solo}
              />
              <Interruptor
                etiqueta="Próximamente"
                valor={f.isComingSoon}
                onCambio={(v) => poner({ isComingSoon: v })}
                deshabilitado={solo}
              />
              <Interruptor
                etiqueta="Agotada"
                valor={f.isSoldOut}
                onCambio={(v) => poner({ isSoldOut: v })}
                deshabilitado={solo}
              />
              <Interruptor
                etiqueta="Resaltar (pulso)"
                valor={f.hasPulse}
                onCambio={(v) => poner({ hasPulse: v })}
                deshabilitado={solo}
              />
              <Interruptor
                etiqueta="Se puede regalar"
                valor={f.isGiftable}
                onCambio={(v) => poner({ isGiftable: v })}
                deshabilitado={solo}
              />
              <Interruptor
                etiqueta="Mostrar «últimos lugares»"
                valor={f.lastSpotsEnabled}
                onCambio={(v) => poner({ lastSpotsEnabled: v })}
                deshabilitado={solo}
              />
            </div>
            <div className="pt-1">
              <Interruptor
                etiqueta="Licencia para instructores"
                valor={f.isInstructor}
                onCambio={(v) => poner({ isInstructor: v })}
                deshabilitado={solo}
              />
              <p className="mt-1 text-xs text-texto-3">
                Para dar acceso a instructores a contenidos específicos para validarlos; no se vende
                en la tienda.
              </p>
            </div>
            {f.lastSpotsEnabled && (
              <Campo etiqueta="Lugares restantes">
                <input
                  type="number"
                  min={0}
                  className="input-vidrio max-w-40"
                  disabled={solo}
                  value={f.lastSpotsCount ?? 0}
                  onChange={(e) => poner({ lastSpotsCount: Number(e.target.value) })}
                />
              </Campo>
            )}
          </Seccion>
          <Seccion titulo="Clases">
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo etiqueta="Límite de reproducciones por clase" ayuda="0 = sin límite">
                <input
                  type="number"
                  min={0}
                  className="input-vidrio"
                  disabled={solo}
                  value={f.classLimit}
                  onChange={(e) => poner({ classLimit: Number(e.target.value) })}
                />
              </Campo>
              <Campo etiqueta="Límite de grabaciones (histórico)">
                <input
                  type="number"
                  min={0}
                  className="input-vidrio"
                  disabled={solo}
                  value={f.liveHistoryLimit}
                  onChange={(e) => poner({ liveHistoryLimit: Number(e.target.value) })}
                />
              </Campo>
            </div>
            <Interruptor
              etiqueta="Aplicar el límite de reproducciones"
              valor={f.enforcesClassLimit}
              onCambio={(v) => poner({ enforcesClassLimit: v })}
              deshabilitado={solo}
            />
          </Seccion>
          <Seccion titulo="Sitio público (consaludenarm.com)">
            <Campo
              etiqueta="Slug público (consaludenarm.com)"
              error={erroresSitio.slug}
              ayuda={`Dirección en el sitio: consaludenarm.com/comprar/${f.slug || '{slug}'}/. Sin slug la licencia no aparece en el sitio. Cambiarlo rompe enlaces ya compartidos.`}
            >
              <input
                className="input-vidrio"
                maxLength={60}
                placeholder="curso-enarm-2027"
                disabled={solo}
                value={f.slug ?? ''}
                onChange={(e) => {
                  setSlugManual(true);
                  poner({ slug: e.target.value || null });
                }}
              />
            </Campo>
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo
                etiqueta="Meses sin intereses (sitio)"
                error={erroresSitio.installments}
                ayuda="Solo si están contratados en PayPal; vacío = no se anuncian."
              >
                <input
                  type="number"
                  min={2}
                  max={24}
                  step={1}
                  className="input-vidrio"
                  disabled={solo}
                  value={f.installments ?? ''}
                  onChange={(e) =>
                    poner({ installments: e.target.value === '' ? null : Number(e.target.value) })
                  }
                />
              </Campo>
              <Campo
                etiqueta="Fin del curso"
                ayuda="Fin del ciclo (normalmente 30 de septiembre): la licencia comprada vence ese día y solo se vende desde el 1 de octubre anterior hasta esta fecha, en el sitio, la plataforma y la app. Vacío = 12 meses desde la compra."
              >
                <input
                  type="date"
                  className="input-vidrio"
                  disabled={solo}
                  value={f.accessEndsOn ?? ''}
                  onChange={(e) => poner({ accessEndsOn: e.target.value || null })}
                />
              </Campo>
            </div>
          </Seccion>
          <Seccion titulo="Curso presencial (opcional)">
            <div className="grid gap-3 sm:grid-cols-2">
              <Campo etiqueta="Desde">
                <input
                  type="date"
                  className="input-vidrio"
                  disabled={solo}
                  value={f.onsiteFrom ?? ''}
                  onChange={(e) => poner({ onsiteFrom: e.target.value || null })}
                />
              </Campo>
              <Campo etiqueta="Hasta">
                <input
                  type="date"
                  className="input-vidrio"
                  disabled={solo}
                  value={f.onsiteTo ?? ''}
                  onChange={(e) => poner({ onsiteTo: e.target.value || null })}
                />
              </Campo>
              <Campo etiqueta="Horario de inicio">
                <input
                  type="time"
                  className="input-vidrio"
                  disabled={solo}
                  value={f.scheduleStart ?? ''}
                  onChange={(e) => poner({ scheduleStart: e.target.value || null })}
                />
              </Campo>
              <Campo etiqueta="Horario de fin">
                <input
                  type="time"
                  className="input-vidrio"
                  disabled={solo}
                  value={f.scheduleEnd ?? ''}
                  onChange={(e) => poner({ scheduleEnd: e.target.value || null })}
                />
              </Campo>
            </div>
          </Seccion>
          <div className="xl:col-span-2">{botonGuardar}</div>
        </div>
      )}

      {pestana === 'descripcion' && (
        <Seccion titulo="Descripción en la tienda">
          <p className="text-sm text-texto-3">
            Formato del legacy: <code>*negritas*</code>, <code>_cursivas_</code>, <code>#</code>{' '}
            para párrafo y <code>=</code> para viñeta con ✔. El editor enriquecido llega en la Fase
            6.
          </p>
          <textarea
            className="input-vidrio min-h-80 font-mono text-sm"
            disabled={solo}
            value={f.description}
            onChange={(e) => poner({ description: e.target.value })}
          />
          {botonGuardar}
        </Seccion>
      )}

      {pestana === 'permisos' && (
        <Seccion titulo="Qué incluye la licencia">
          {permisosCat.isPending && <Cargando />}
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {grupos.map(([grupo, lista]) => (
              <fieldset key={grupo} className="space-y-2">
                <legend className="mb-1 text-xs font-extrabold tracking-wider text-texto-3 uppercase">
                  {grupo}
                </legend>
                {lista.map((p) => (
                  <Interruptor
                    key={p.key}
                    etiqueta={p.name}
                    valor={permisos.includes(p.key)}
                    deshabilitado={solo}
                    onCambio={(v) =>
                      setPermisos((ps) => (v ? [...ps, p.key] : ps.filter((x) => x !== p.key)))
                    }
                  />
                ))}
              </fieldset>
            ))}
          </div>
          {editable && (
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => guardarPermisos.mutate()}
              disabled={guardarPermisos.isPending}
            >
              Guardar permisos
            </button>
          )}
        </Seccion>
      )}

      {pestana === 'obsequios' && (
        <Seccion titulo="Licencias de obsequio">
          <p className="text-sm text-texto-2">
            Se otorgan con las mismas fechas al comprar o al otorgar esta licencia.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {opciones.data
              ?.filter((o) => o.id !== actual?.id)
              .map((o) => (
                <Interruptor
                  key={o.id}
                  etiqueta={`${o.name}${o.isActive ? '' : ' (inactiva)'}`}
                  valor={regalos.includes(o.id)}
                  deshabilitado={solo}
                  onCambio={(v) =>
                    setRegalos((rs) => (v ? [...rs, o.id] : rs.filter((x) => x !== o.id)))
                  }
                />
              ))}
          </div>
          {editable && (
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => guardarRegalos.mutate()}
              disabled={guardarRegalos.isPending}
            >
              Guardar obsequios
            </button>
          )}
        </Seccion>
      )}

      {pestana === 'equipos' && (
        <Seccion titulo="Precio por persona según el tamaño del equipo">
          <table className="tabla-cs w-full max-w-xl text-sm">
            <thead>
              <tr>
                <th>Integrantes</th>
                <th>Precio por persona (MXN)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tiers.map((t, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="number"
                      min={2}
                      className="input-vidrio"
                      disabled={solo}
                      value={t.membersCount}
                      onChange={(e) =>
                        setTiers((ts) =>
                          ts.map((x, j) =>
                            j === i ? { ...x, membersCount: Number(e.target.value) } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="input-vidrio"
                      inputMode="decimal"
                      disabled={solo}
                      value={t.finalPrice}
                      onChange={(e) =>
                        setTiers((ts) =>
                          ts.map((x, j) => (j === i ? { ...x, finalPrice: e.target.value } : x)),
                        )
                      }
                    />
                  </td>
                  <td>
                    {editable && (
                      <button
                        type="button"
                        className="rounded-full p-1.5 hover:bg-[var(--pista)]"
                        aria-label="Quitar"
                        onClick={() => setTiers((ts) => ts.filter((_x, j) => j !== i))}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {editable && (
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-cs btn-contorno"
                onClick={() =>
                  setTiers((ts) => [
                    ...ts,
                    { membersCount: (ts.at(-1)?.membersCount ?? 1) + 1, finalPrice: f.finalPrice },
                  ])
                }
              >
                <Plus size={16} aria-hidden /> Agregar fila
              </button>
              <button
                type="button"
                className="btn-cs btn-primario"
                onClick={() => guardarTiers.mutate()}
                disabled={guardarTiers.isPending}
              >
                Guardar precios
              </button>
            </div>
          )}
        </Seccion>
      )}

      {pestana === 'appstore' && (
        <Seccion titulo="App Store y Google Play">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Precio en la app (MXN)">
              <input
                className="input-vidrio"
                inputMode="decimal"
                disabled={solo}
                value={f.appStorePrice ?? ''}
                onChange={(e) => poner({ appStorePrice: e.target.value })}
              />
            </Campo>
            <div />
            <Campo etiqueta="ID de producto App Store">
              <input
                className="input-vidrio"
                disabled={solo}
                value={f.appStoreProductId ?? ''}
                onChange={(e) => poner({ appStoreProductId: e.target.value || null })}
              />
            </Campo>
            <Campo etiqueta="ID de producto Google Play">
              <input
                className="input-vidrio"
                disabled={solo}
                value={f.googlePlayProductId ?? ''}
                onChange={(e) => poner({ googlePlayProductId: e.target.value || null })}
              />
            </Campo>
          </div>
          <Campo etiqueta="Descripción en la app (máx. 250)">
            <textarea
              className="input-vidrio min-h-28"
              maxLength={250}
              disabled={solo}
              value={f.appStoreDescription ?? ''}
              onChange={(e) => poner({ appStoreDescription: e.target.value || null })}
            />
          </Campo>
          {botonGuardar}
        </Seccion>
      )}
    </>
  );
}

export function LicenciaPage() {
  const { id } = useParams();
  const numero = id && id !== 'nueva' ? Number(id) : null;
  const q = useLicencia(numero);
  return (
    <PermissionGate seccion="tienda" nivel={numero === null ? 'editar' : 'ver'}>
      {numero === null ? (
        <Editor actual={null} />
      ) : q.isPending ? (
        <Cargando />
      ) : q.isError ? (
        <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />
      ) : (
        <Editor key={`${q.data.id}-${q.data.updatedAt ?? ''}`} actual={q.data} />
      )}
    </PermissionGate>
  );
}
